/**
 * "Who grinds harder?" — what each of you ADDED, dex by dex, over a period.
 *
 * The chart above answers who is ahead. This answers who has been putting the
 * work in, which is a different question and a kinder one: a trainer six
 * hundred behind can still have out-grown the leader this week, and on a page
 * whose whole subject is the two of you that is worth seeing. It is also the
 * only surface that says anything about the dexes nobody is winning.
 *
 * THE LAYOUT IS THE ARGUMENT. The dex name runs down the middle with your gain
 * to its left and theirs to its right, so the eye reads across one row as a
 * head-to-head rather than down two separate columns. The bigger number of the
 * pair is bold and coloured; a draw bolds neither, because a tie is not a win
 * and half the rows are ties (nobody caught a Shundo this week).
 *
 * Periods are week / fortnight / month, in the same pill shape the chart's
 * window switcher uses — the two controls do the same kind of job a few
 * hundred pixels apart, and looking different would imply they don't.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { DEX_VARIANT_META, type DexVariantKey } from "@/components/dexVariantVisuals";
import { formatDayKey, friendLineColor } from "@/lib/dexHistoryChart";

const PERIODS = [
  { key: "7", label: "Week" },
  { key: "14", label: "Fortnight" },
  { key: "30", label: "Month" },
] as const;

interface GrindRow {
  variant: string;
  mine: number;
  theirs: number;
}

interface GrindResponse {
  days: number;
  since: string;
  /**
   * "observed"  — every figure is the difference between two readings the app
   *               took. Exact.
   * "estimated" — the reconstruction is in play, so a figure counts when a
   *               Pokémon was first RECORDED. Must be labelled.
   *
   * Decided server-side on coverage; the client cannot see the sources.
   */
  basis?: "observed" | "estimated" | "any";
  /**
   * The day this comparison starts being fair — the later of the two trainers'
   * first OBSERVED days. Null when one of you has none yet.
   */
  comparableFrom: string | null;
  rows: GrindRow[];
}

/**
 * Dates read as `05Aug`, not `08-05`. A bare month-day pair is ambiguous to
 * most of the world and unreadable next to a count; a named month cannot be
 * mistaken for a day. Shared with the chart above so the two agree.
 */
const shortDay = formatDayKey;

/** A gain, as it is written on the board. Zero is a dash — nothing happened. */
function gainText(n: number): string {
  return n > 0 ? `+${n}` : "-";
}

export function GrindBoard({ friendId, friendName }: { friendId: number; friendName: string }) {
  const [days, setDays] = useState<string>("7");

  const url = `/api/friends/${friendId}/grind?days=${days}`;
  const q = useQuery<GrindResponse>({
    queryKey: [url],
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const mineColor = "var(--accent-fill)";
  const theirsColor = friendLineColor(friendId);
  const rows = q.data?.rows ?? [];
  const mineTotal = rows.reduce((sum, r) => sum + r.mine, 0);
  const theirsTotal = rows.reduce((sum, r) => sum + r.theirs, 0);
  const anyMovement = mineTotal > 0 || theirsTotal > 0;

  return (
    <section className="v2-neon-card-soft p-4 mt-4" data-testid="section-grind-board">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="v2-eyebrow" data-testid="eyebrow-grind-board">
          <span className="v2-eyebrow__dot" aria-hidden />
          Who grinds harder?
        </span>
        <div className="flex gap-1 shrink-0">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setDays(p.key)}
              aria-pressed={days === p.key}
              data-testid={`grind-period-${p.key}`}
              className="rounded-full px-2 py-0.5 text-[10px] font-bold"
              style={{
                background: days === p.key ? "var(--accent-fill)" : "transparent",
                color: days === p.key ? "var(--text-on-accent)" : "var(--text-tertiary)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {/*
        SAY WHAT IS BEING COUNTED, which is not always the period you picked.
        Only days the app actually WATCHED count towards a gain — a
        reconstructed day is built from when dex rows were recorded, so the
        afternoon someone scanned their collection in would read as a colossal
        day of catching and the board would rank whoever scanned most recently.
        When tracking started partway through the chosen period, the honest
        headline is the day it started, not the day the period did.
      */}
      <p className="text-[10.5px] mb-3" style={{ color: "var(--text-secondary)" }} data-testid="grind-window-note">
        {q.data == null
          ? "What each of you has added over this period."
          : q.data.basis === "observed"
            ? `What each of you has added since ${shortDay(q.data.since)}, counted as it happened.`
            : `Estimated. Day-by-day tracking is newer than this window, so a figure counts when a Pokémon was first recorded: scanning a dex in shows up as a big day. Exact from ${q.data.comparableFrom ? shortDay(q.data.comparableFrom) : "the next time you each scan"}.`}
      </p>

      {/* Who is who, in the same two colours the chart's lines use.
          THREE COLUMNS, and the middle one is a FIXED width. It was `auto`,
          which is a different width on every row — "Main" against "Shiny
          Lucky" — so the two number columns moved in and out with it and
          nothing lined up down the card. Everything ended up huddled around the
          centre with the card's whole width empty on either side. Reported on
          device 2026-08-12. Fixing the middle column is what lets the numbers
          form real columns, and centring them in their own puts them under
          their own heading instead of against the label. */}
      <div
        className="grid grid-cols-[1fr_7.5rem_1fr] gap-2 items-center pb-1.5"
        style={{ borderBottom: "1px solid var(--border-subtle)" }}
      >
        <span className="text-[10px] font-bold text-center truncate" style={{ color: mineColor }}>
          You
        </span>
        <span
          className="text-[9px] uppercase tracking-wider text-center"
          style={{ color: "var(--text-tertiary)" }}
        >
          Dex
        </span>
        <span className="text-[10px] font-bold text-center truncate" style={{ color: theirsColor }}>
          {friendName}
        </span>
      </div>

      {q.isLoading ? (
        <p className="text-[11.5px]" style={{ color: "var(--text-tertiary)" }} data-testid="grind-loading">
          Working out who's been busier…
        </p>
      ) : q.isError ? (
        <p className="text-[11.5px]" style={{ color: "var(--text-tertiary)" }} data-testid="grind-error">
          Couldn't load this. Pull again in a moment.
        </p>
      ) : (
        <>
          <ul data-testid="grind-rows">
            {rows.map((r, i) => {
              const meta = DEX_VARIANT_META[r.variant as DexVariantKey];
              const iWin = r.mine > r.theirs;
              const theyWin = r.theirs > r.mine;
              const quiet = r.mine === 0 && r.theirs === 0;
              return (
                <li
                  key={r.variant}
                  className="grid grid-cols-[1fr_7.5rem_1fr] gap-2 items-center py-1.5"
                  style={{
                    opacity: quiet ? 0.45 : 1,
                    // A hairline between rows, so twelve of them read as a table
                    // rather than as text floating in a card.
                    ...(i > 0 ? { borderTop: "1px solid var(--border-subtle)" } : {}),
                  }}
                  data-testid={`grind-row-${r.variant}`}
                  data-winner={iWin ? "mine" : theyWin ? "theirs" : undefined}
                >
                  <span
                    className="text-center tabular-nums text-[13px]"
                    style={{
                      color: iWin ? mineColor : "var(--text-secondary)",
                      fontWeight: iWin ? 800 : 500,
                    }}
                    data-testid={`grind-mine-${r.variant}`}
                  >
                    {gainText(r.mine)}
                  </span>
                  <span
                    className="text-[10.5px] font-semibold text-center whitespace-nowrap"
                    style={{ color: meta ? `hsl(${meta.token})` : "var(--text-tertiary)" }}
                  >
                    {meta?.label ?? r.variant}
                  </span>
                  <span
                    className="text-center tabular-nums text-[13px]"
                    style={{
                      color: theyWin ? theirsColor : "var(--text-secondary)",
                      fontWeight: theyWin ? 800 : 500,
                    }}
                    data-testid={`grind-theirs-${r.variant}`}
                  >
                    {gainText(r.theirs)}
                  </span>
                </li>
              );
            })}
          </ul>

          {/* The verdict, stated once. Reading twelve rows to work out who won
              is work the page can do for you. */}
          <div
            className="grid grid-cols-[1fr_7.5rem_1fr] gap-2 items-center mt-2 pt-2"
            style={{ borderTop: "1px solid var(--border-subtle)" }}
            data-testid="grind-total"
          >
            <span
              className="text-center tabular-nums text-[14px]"
              style={{ color: mineTotal > theirsTotal ? mineColor : "var(--text-secondary)", fontWeight: mineTotal > theirsTotal ? 800 : 600 }}
            >
              {gainText(mineTotal)}
            </span>
            <span className="text-[9px] uppercase tracking-wider text-center" style={{ color: "var(--text-tertiary)" }}>
              All dexes
            </span>
            <span
              className="text-center tabular-nums text-[14px]"
              style={{ color: theirsTotal > mineTotal ? theirsColor : "var(--text-secondary)", fontWeight: theirsTotal > mineTotal ? 800 : 600 }}
            >
              {gainText(theirsTotal)}
            </span>
          </div>

          {!anyMovement && (
            <p className="text-[11px] mt-2" style={{ color: "var(--text-tertiary)" }} data-testid="grind-quiet">
              Neither of you has added anything in this window. Try a longer one.
            </p>
          )}
        </>
      )}
    </section>
  );
}
